import { macroCondition, isTesting } from '@embroider/macros';
import { dropTask, timeout } from 'ember-concurrency';
import { PhDeviceMobile, PhPaperPlaneTilt, PhWarningCircle } from 'ember-phosphor-icons';
import {
  checkPhoneVerificationRequest,
  parsePhoneVerificationResult,
  phoneVerificationAttempt,
  phoneVerificationErrorCode,
  requestPhoneVerificationLinkRequest,
  startPhoneVerificationRequest,
  submitPhoneVerificationRequest,
} from 'tio-data/requests/phone-number';
import { modifier } from 'ember-modifier';
import { get } from '@ember/helper';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import AlertBanner from 'tio-ui/components/alert-banner';
import Button from 'tio-ui/components/button';
import CodeInput from 'tio-ui/components/code-input';
import Component from '@glimmer/component';
import Heading from 'tio-ui/components/heading';
import matchMedia from 'tio-common/helpers/match-media';
import IconBadge from 'tio-ui/components/icon-badge';
import PhoneInput, { DEFAULT_COUNTRY, emptyPhone, readPhone } from 'tio-ui/components/phone-input';
import type IntlService from 'ember-intl/services/intl';
import type PhoneCaptureService from 'tio-employee/services/phone-capture';
import type {
  PhoneVerificationAttemptPayload,
  PhoneVerificationCheckResult,
  PhoneVerificationPlatform,
  PhoneVerificationResult,
  PhoneVerificationSubmitResult,
  PhoneVerificationSurface,
} from 'tio-data/requests/phone-number';
import type { PhoneCountryCode, PhoneValue } from 'tio-ui/components/phone-input';
import type { Store as StoreService } from '@warp-drive/core';

export type FlowStep = 'enter-number' | 'code' | 'verified' | 'deferred';

/** Where a surface may open the flow. Terminal steps are reached, never entered. */
export type FlowEntryStep = Extract<FlowStep, 'enter-number' | 'code'>;
export type FlowSize = 'sm' | 'md' | 'lg';

/** Matches CodeInput's default; the server accepts 4-10 but the UI issues six. */
const CODE_LENGTH = 6;

/** The five RFC 110 surfaces, sent with each request so the funnel is attributed. */
export type FlowSurface = PhoneVerificationSurface;

/**
 * The only refusal that resolves to verify-later. `rate_limit_exceeded` is the resend
 * cooldown and the abuse limiter — a wait, so it keeps the member on the step.
 */
const LOCKOUT_ERROR_CODE = 'verification_locked_out';

/**
 * The retry/cooldown/lockout facts the server owns. Every one stays optional: a
 * response that omits them degrades to "no countdown shown" rather than inventing one.
 * Nothing here is ever hardcoded — a client constant would drift from Twilio Verify's
 * hosted TTL the moment either changed.
 */
export type VerificationMeta = PhoneVerificationAttemptPayload;

/**
 * The interval lives in a modifier so Ember tears it down with the element; a
 * class-held timer outlives a flow that unmounts mid-countdown.
 */
const ticking = modifier((_element: HTMLElement, [onTick]: [() => void]) => {
  const id = setInterval(onTick, 1000);

  return () => clearInterval(id);
});

/**
 * Sends once on insert. A surface that has already collected the number wants the code
 * on its way without a second tap; a refusal leaves the flow on the number step with
 * its own error, which is the only safe landing.
 */
const sendOnInsert = modifier((_element: HTMLElement, [send]: [() => void]) => {
  send();
});

/**
 * Long enough to read the confirmation, short enough that a surface which navigates away
 * does not feel stuck. The test build keeps the hold but not its length: the tests prove
 * the confirmation shows before the hand-off, and waiting 1.6 s per test proved nothing more.
 */
const CONFIRMATION_HOLD_MS = macroCondition(isTesting()) ? 25 : 1600;

export type FlowEntryLayout = 'inline' | 'stacked';

/** Mirrors FormField's LABEL_SIZES: the inline row's spacer has to match the label it stands in for. */
const LABEL_TYPE: Record<string, string> = {
  sm: 'tio-text-xs-medium',
  md: 'tio-text-sm-medium',
  lg: 'tio-text-sm-medium',
};

export interface PhoneVerificationFlowSignature {
  Args: {
    /** Prefills the number step; omit to collect one. */
    phoneNumber?: string;
    initialStep?: FlowEntryStep;
    /**
     * Send to `@phoneNumber` as soon as the flow renders, instead of waiting for a tap.
     * Only meaningful with a number already supplied.
     */
    autoSend?: boolean;
    /**
     * Region a bare `@phoneNumber` was collected under: ten Mexican digits look exactly
     * like ten US ones. A number carrying its own dial code needs none.
     */
    country?: PhoneCountryCode;
    size?: FlowSize;
    /** Attribution only — the component emits no analytics of its own. */
    surface: FlowSurface;
    onVerified?: (result: PhoneVerificationCheckResult) => void;
    /** Fired when the flow resolves to verify-later, including on lockout. */
    onDeferred?: () => void;
    onChangeNumber?: () => void;
    /** Per-surface confirmation copy; only enrollment's is designed today. */
    confirmationTitle?: string;
    confirmationBody?: string;
    /** The surface's own pitch, rendered under the number step's heading. */
    intro?: string;
    /** `inline` puts the field and Send on one row; the default stacks them. */
    entryLayout?: FlowEntryLayout;
    /** Drops each step's badge and heading, for a host that shows its own. */
    headless?: boolean;
  };
  Element: HTMLDivElement;
}

export default class PhoneVerificationFlow extends Component<PhoneVerificationFlowSignature> {
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @service declare phoneCapture: PhoneCaptureService;

  @tracked private stepOverride?: FlowStep;
  /** What the field last reported; undefined until the member touches it, so a supplied
   * number can prefill. */
  @tracked private typed?: PhoneValue;
  @tracked code = '';
  @tracked errorMessage = '';
  @tracked private meta: VerificationMeta = {};
  @tracked private codeRejected = false;
  /** Re-read on every tick so the countdown getters recompute; see `ticking`. */
  @tracked private now = Date.now();

  /**
   * Entry is number-first unless a caller explicitly resumes at `code`. A known
   * `@phoneNumber` only prefills the field: defaulting to the code step rendered "We
   * sent a code" for a challenge this flow had never started, since nothing but the
   * send button and resend perform one.
   */
  get step(): FlowStep {
    return this.stepOverride ?? this.args.initialStep ?? 'enter-number';
  }

  /** Passed to CodeInput so the boxes are announced invalid, not just the banner. */
  get codeErrors(): string[] {
    return this.errorMessage ? [this.errorMessage] : [];
  }

  /** The code step submits a full code only; a short one would spend a server attempt. */
  get isCodeIncomplete(): boolean {
    return this.code.length < CODE_LENGTH;
  }

  /** The media query the mobile default resolves against, matching Tailwind's `md`. */
  desktopQuery = '(min-width: 768px)';

  /** The field still shows whatever the surface supplied; nothing typed yet. */
  private get isUntouched() {
    return this.typed === undefined;
  }

  /** The supplied number as the field reads it, under the region the surface named. */
  private get prefill(): PhoneValue | undefined {
    return this.args.phoneNumber
      ? readPhone(this.args.phoneNumber, this.args.country ?? DEFAULT_COUNTRY)
      : undefined;
  }

  /**
   * The number on the step. Once touched it is whatever the member typed — including
   * empty — so backspacing a prefilled number does not snap it back and "Change
   * number" does not re-offer the number they just rejected.
   */
  get phone(): PhoneValue | undefined {
    return this.typed ?? this.prefill;
  }

  /**
   * What the member is told the code went to. submitNumber is E.164 for the wire; a
   * NANP number reads back as it is dialled locally, anything else as dialled.
   */
  get destinationDisplay(): string {
    const number = this.submitNumber ?? '';
    const nanp = number.match(/^\+1(\d{3})(\d{3})(\d{4})$/);

    return nanp ? `(${nanp[1]}) ${nanp[2]}-${nanp[3]}` : number;
  }

  /** What gets sent: E.164 only, so the server never has to guess a region. */
  get submitNumber(): string | undefined {
    return this.phone?.e164 ?? undefined;
  }

  get attemptsRemaining(): number | undefined {
    return this.meta.attemptsRemaining;
  }

  /**
   * The server sends attemptsRemaining on every response, so this gates on a rejection
   * having happened — a warning about a failure that has not is a threat.
   */
  get shouldWarnAttempts(): boolean {
    return this.codeRejected && (this.attemptsRemaining ?? 0) > 0;
  }

  /**
   * Locked out when the server says so. Absent means not locked — never assume a
   * lockout the server has not declared.
   */
  get isLockedOut(): boolean {
    return this.lockoutSecondsRemaining > 0;
  }

  /** Whole seconds until the server will accept another send; 0 when resend is free. */
  get resendSecondsRemaining(): number {
    return this.secondsUntil(this.meta.resendAvailableAt);
  }

  get lockoutSecondsRemaining(): number {
    return this.secondsUntil(this.meta.lockedUntil);
  }

  get codeExpirySecondsRemaining(): number {
    return this.secondsUntil(this.meta.codeExpiresAt);
  }

  get codeExpiryDisplay(): string {
    return PhoneVerificationFlow.asClock(this.codeExpirySecondsRemaining);
  }

  get lockoutDisplay(): string {
    return PhoneVerificationFlow.asClock(this.lockoutSecondsRemaining);
  }

  get isResendBlocked(): boolean {
    return this.resendSecondsRemaining > 0;
  }

  /** Minutes and seconds, because these windows are ten and fifteen minutes long. */
  private static asClock(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);

    return `${minutes}:${String(totalSeconds % 60).padStart(2, '0')}`;
  }

  /** Rounds up, so a countdown never displays 0 while the cooldown is still in force. */
  private secondsUntil(timestamp?: string): number {
    if (!timestamp) {
      return 0;
    }

    const remaining = new Date(timestamp).getTime() - this.now;

    return remaining > 0 ? Math.ceil(remaining / 1000) : 0;
  }

  get isSubmitDisabled(): boolean {
    return !this.submitNumber || this.sendToNumber.isRunning || this.isResendBlocked;
  }

  /**
   * A code-step request is in flight. The actions there are mutually exclusive: a
   * resend landing after a successful check would reset the step to `code`, and
   * deferring mid-check fires both callbacks for one flow.
   */
  get isBusy(): boolean {
    return this.submitCode.isRunning || this.resend.isRunning;
  }

  get isVerifyDisabled(): boolean {
    return this.isCodeIncomplete || this.isBusy;
  }

  get isResendDisabled(): boolean {
    return this.isBusy || this.isResendBlocked;
  }

  /**
   * The code step announces failures through the field, which also carries
   * `aria-invalid` and the error association. Repeating them in the banner's live
   * region would announce one rejected code twice.
   */
  get bannerMessage(): string {
    return this.isStep('code') ? '' : this.errorMessage;
  }

  /**
   * Confirming, not adding — but only while the field still holds the dialable number
   * the surface supplied. "Change number" empties it, and an undialable prefill was
   * never a number to confirm.
   */
  get isConfirmingSuppliedNumber() {
    return this.isUntouched && Boolean(this.phone?.e164);
  }

  get numberTitle() {
    const key = this.isConfirmingSuppliedNumber ? 'number.title_existing' : 'number.title';

    return this.intl.t(`phone_verification.${key}`);
  }

  get isInlineEntry() {
    return this.args.entryLayout === 'inline';
  }

  /** The pitch is spent once they have acted on it; returning to change a number is not a
   * fresh ask. */
  get showsIntro() {
    return Boolean(this.args.intro) && this.stepOverride !== 'enter-number' && !this.hasAutoSent;
  }

  /** Template-friendly step predicate, so each branch reads as its own name. */
  isStep = (step: FlowStep): boolean => this.step === step;

  /**
   * Read at request time rather than tracked: the funnel wants the viewport the member
   * actually acted in, and a mid-flow resize should not retag the attempt.
   */
  private get platform(): PhoneVerificationPlatform {
    return window.matchMedia(this.desktopQuery).matches ? 'desktop' : 'mobile';
  }

  /** Attribution attached to every command, so the funnel can group by surface. */
  private get origin() {
    return { surface: this.args.surface, platform: this.platform };
  }

  /**
   * Persists the number as `unverified` *and* sends the first code, unless it is the
   * verified number already on file, which the server confirms without sending one.
   * Submitting rather than only starting is what makes abandoning the code step
   * survivable — the number is on file, so the deferred link has somewhere to point.
   */
  sendToNumber = dropTask(async () => {
    const phone = this.phone;
    if (!phone?.e164) {
      return;
    }

    this.errorMessage = '';
    try {
      const { content } = await this.store.request(
        submitPhoneVerificationRequest(phone.e164, { ...this.origin, country: phone.country })
      );
      const result = parsePhoneVerificationResult<PhoneVerificationSubmitResult>(content);
      this.applyMeta(result);
      if (result.challengeState === 'verified') {
        // The number on file was already verified, so the server sent nothing to enter.
        this.confirmAlreadyVerified(result);
        return;
      }
      this.stepOverride = 'code';
      this.code = '';
    } catch (error: unknown) {
      this.handleRefusal(error);
    }
  });

  /**
   * Every surface closes or navigates the moment it is told, so telling it in the same
   * turn as the step change unmounts the confirmation before it paints. A teardown
   * inside the hold cancels the task, which is correct: the callback only moves the UI,
   * and prompt completion is recorded server-side off the verify transition.
   */
  private holdThenHandOff = dropTask(async (notify: () => void) => {
    await timeout(CONFIRMATION_HOLD_MS);
    notify();
  });

  private confirmAlreadyVerified(result: PhoneVerificationSubmitResult) {
    const phoneNumberId = result.phoneNumber?.id;
    if (!phoneNumberId) {
      this.errorMessage = this.intl.t('phone_verification.errors.unknown');
      return;
    }

    this.confirmVerified({ ...result, phoneNumberId, phoneNumberChanged: false });
  }

  private confirmVerified(result: PhoneVerificationCheckResult) {
    this.stepOverride = 'verified';
    this.holdThenHandOff.perform(() => this.args.onVerified?.(result));
  }

  submitCode = dropTask(async (code?: string) => {
    const submitted = code ?? this.code;
    this.errorMessage = '';

    try {
      const { content } = await this.store.request(
        checkPhoneVerificationRequest(submitted, this.submitNumber, this.origin)
      );
      const result = parsePhoneVerificationResult<PhoneVerificationCheckResult>(content);
      this.applyMeta(result);
      this.confirmVerified(result);
    } catch (error: unknown) {
      this.handleFailedCode(error);
    }
  });

  /** Re-sends to the number already on file; the number step owns storing it. */
  resend = dropTask(async () => {
    this.code = '';
    this.errorMessage = '';

    try {
      const { content } = await this.store.request(
        startPhoneVerificationRequest(this.submitNumber, this.origin)
      );
      this.applyMeta(parsePhoneVerificationResult<PhoneVerificationResult>(content));
    } catch (error: unknown) {
      this.handleRefusal(error);
    }
  });

  /**
   * Countdown and attempt figures come from the server on every response, so a
   * response that omits `verification` clears stale numbers instead of freezing them.
   */
  private applyMeta(result: PhoneVerificationResult) {
    this.meta = result.verification ?? {};
    this.now = Date.now();
  }

  tick = () => {
    this.now = Date.now();
  };

  private handleFailedCode(error: unknown) {
    this.code = '';
    this.codeRejected = true;
    this.handleRefusal(error);
  }

  /**
   * Adopts the countdowns the rejection carries, so a refused resend cannot re-enable on
   * the previous response's cooldown. Defers only once the attempts are spent.
   */
  private handleRefusal(error: unknown) {
    this.errorMessage = this.messageFor(error);

    const attempt = phoneVerificationAttempt(error);
    if (attempt) {
      this.meta = attempt;
      this.now = Date.now();
    }

    if (phoneVerificationErrorCode(error) === LOCKOUT_ERROR_CODE || this.lockoutReached) {
      this.deferVerification();
    }
  }

  /** True only once the server tells us; absent countdown data never implies a lockout. */
  private get lockoutReached(): boolean {
    return this.isLockedOut || this.attemptsRemaining === 0;
  }

  private requestLink = dropTask(async () => {
    try {
      await this.store.request(requestPhoneVerificationLinkRequest(this.origin));
    } catch {
      // No mobile on file, rate limited, or unreachable: the deferral stands regardless.
    }
  });

  deferVerification = () => {
    // Not awaited: a link that cannot be sent must not hold the member on this step.
    this.requestLink.perform();
    // The confirmation is a way forward, so it must not carry the failure banner.
    this.errorMessage = '';
    this.stepOverride = 'deferred';
    this.holdThenHandOff.perform(() => this.args.onDeferred?.());
  };

  changeNumber = () => {
    this.code = '';
    this.codeRejected = false;
    this.errorMessage = '';
    this.typed = emptyPhone(this.resetRegion);
    this.stepOverride = 'enter-number';
    this.args.onChangeNumber?.();
  };

  /** The number's own region while it is still on offer, else the first one that is. */
  private get resetRegion(): PhoneCountryCode {
    const previous = this.prefill?.country ?? this.args.country ?? DEFAULT_COUNTRY;
    const regions = this.phoneCapture.regions;

    return regions.includes(previous) ? previous : (regions[0] ?? DEFAULT_COUNTRY);
  }

  updateNumber = (value: PhoneValue) => {
    this.typed = value;
    this.errorMessage = '';
  };

  /** `codeRejected` outlives the message so the attempts warning survives the retype. */
  updateCode = (value: string) => {
    this.code = value;
    this.errorMessage = '';
  };

  /** The button submits whatever is typed; CodeInput's onComplete passes the code. */
  submitTypedCode = () => {
    this.submitCode.perform();
  };

  /** Editing the number clears `submitNumber` and a blur restores it, so deriving this
   * from render state alone re-sends on every blur. */
  @tracked private hasAutoSent = false;

  /**
   * Only for the supplied number, once, and never past the entry step. A number the member
   * types is theirs to send — auto-sending it fires an SMS they never asked for.
   */
  get shouldAutoSend() {
    return (
      !!this.args.autoSend &&
      !this.hasAutoSent &&
      this.isUntouched &&
      !!this.prefill?.e164 &&
      this.step === 'enter-number'
    );
  }

  autoSend = () => {
    this.hasAutoSent = true;
    this.sendCode();
  };

  sendCode = () => {
    this.sendToNumber.perform();
  };

  resendCode = () => {
    this.resend.perform();
  };

  /**
   * Localised from the server's error `code`, never its `detail` — the detail is
   * English prose written for an operator.
   */
  private messageFor(error: unknown): string {
    const code = phoneVerificationErrorCode(error);

    return code
      ? this.intl.t(`phone_verification.errors.${code}`)
      : this.intl.t('phone_verification.errors.unknown');
  }

  <template>
    {{! An omitted @size resolves per viewport rather than always rendering the desktop
        tier; match-media is the repository's mechanism and re-renders on change. }}
    {{#let (if @size @size (if (matchMedia this.desktopQuery) "md" "sm")) as |size|}}
      <div class="tio-col-loose" data-test-phone-verification-flow={{@surface}} ...attributes>
        {{#if (and (this.isStep "enter-number") this.isResendBlocked)}}
          {{! Only the code and deferred steps carried the clock, so a refusal here disabled
              Send against a countdown that never advanced. }}
          <span
            class="tio-text-sm-normal tio-fg-muted"
            data-test-phone-verification-send-cooldown
            {{ticking this.tick}}
          >{{t "phone_verification.code.resend_in" seconds=this.resendSecondsRemaining}}</span>
        {{/if}}
        {{#if this.shouldAutoSend}}
          <span hidden {{sendOnInsert this.autoSend}}></span>
        {{/if}}
        {{#if this.bannerMessage}}
          <AlertBanner @intent="danger" data-test-flow-error>
            <PhWarningCircle class="inline size-4" />
            {{this.bannerMessage}}
          </AlertBanner>
        {{/if}}

        {{#if (this.isStep "enter-number")}}
          {{#unless @headless}}
            <IconBadge
              @icon={{PhDeviceMobile}}
              @color="teal"
              @size="lg"
              class="self-start"
              data-test-flow-hero
            />
            <Heading @level={{2}} @size="lg">{{this.numberTitle}}</Heading>
          {{/unless}}
          {{#if this.showsIntro}}
            <p class="tio-text-sm-normal" data-test-flow-intro>{{@intro}}</p>
          {{/if}}
          <p class="tio-text-sm-normal" data-test-phone-verification-consent>
            {{htmlSafe (t "phone_verification.number.consent")}}
          </p>

          <div
            class={{if this.isInlineEntry "tio-row items-start" "tio-col-loose"}}
            data-test-phone-verification-entry
          >
            <PhoneInput
              class={{if this.isInlineEntry "grow min-w-0"}}
              @testHook="phone-verification-number"
              @value={{this.phone}}
              @size={{size}}
              @onChange={{this.updateNumber}}
              @defaultCountry={{@country}}
              @countries={{this.phoneCapture.regions}}
              @isDisabled={{this.sendToNumber.isRunning}}
              @label={{t "phone_verification.number.label"}}
            />

            {{#if this.isInlineEntry}}
              {{! Send belongs on the input, and the field grows downward when it reports an
                  error — so the row aligns at the top and a spacer carrying the label's own
                  type class holds Send level with the input at any size. }}
              <div class="tio-col-extra-tight shrink-0">
                <span class={{get LABEL_TYPE size}} aria-hidden="true">&nbsp;</span>
                <Button
                  @intent="primary"
                  @isRunning={{this.sendToNumber.isRunning}}
                  disabled={{this.isSubmitDisabled}}
                  {{on "click" this.sendCode}}
                  data-test-send-code
                >{{t "phone_verification.number.send"}}</Button>
              </div>
            {{else}}
              <Button
                @intent="primary"
                @isRunning={{this.sendToNumber.isRunning}}
                disabled={{this.isSubmitDisabled}}
                {{on "click" this.sendCode}}
                data-test-send-code
              >{{t "phone_verification.number.send"}}</Button>
            {{/if}}
          </div>
        {{/if}}

        {{#if (this.isStep "code")}}
          {{#unless @headless}}
            <IconBadge
              @icon={{PhDeviceMobile}}
              @color="teal"
              @size="lg"
              class="self-start"
              data-test-flow-code-hero
            />
            <Heading @level={{2}} @size="lg">{{t "phone_verification.code.title"}}</Heading>
          {{/unless}}
          <p class="tio-text-sm-normal">
            {{t "phone_verification.code.sent_to"}}
            <strong data-test-code-destination>{{this.destinationDisplay}}</strong>
          </p>

          <Button
            @appearance="minimal"
            @size="sm"
            disabled={{this.isBusy}}
            {{on "click" this.changeNumber}}
            data-test-change-number
          >{{t "phone_verification.code.change_number"}}</Button>

          <CodeInput
            @testHook="phone-verification-code"
            @value={{this.code}}
            @size={{size}}
            @onChange={{this.updateCode}}
            @onComplete={{this.submitCode.perform}}
            @errors={{this.codeErrors}}
            @isDisabled={{this.isBusy}}
            @label={{t "phone_verification.code.label"}}
          />

          {{! One interval drives every countdown in this step. }}
          <div class="tio-col-flush" {{ticking this.tick}}>
            {{#if this.shouldWarnAttempts}}
              <p class="tio-text-sm-normal" data-test-attempts-remaining>
                {{t "phone_verification.code.attempts_left" count=this.attemptsRemaining}}
              </p>
            {{/if}}

            {{#if this.codeExpirySecondsRemaining}}
              <p class="tio-text-sm-normal" data-test-phone-verification-code-expiry>
                {{t "phone_verification.code.expires_in" time=this.codeExpiryDisplay}}
              </p>
            {{/if}}

            {{#if this.isResendBlocked}}
              <p class="tio-text-sm-normal" data-test-phone-verification-resend-countdown>
                {{t "phone_verification.code.resend_in" seconds=this.resendSecondsRemaining}}
              </p>
            {{/if}}
          </div>

          <Button
            @intent="primary"
            @isRunning={{this.submitCode.isRunning}}
            disabled={{this.isVerifyDisabled}}
            {{on "click" this.submitTypedCode}}
            data-test-verify
          >{{t "phone_verification.code.verify"}}</Button>

          <Button
            @appearance="minimal"
            @isRunning={{this.resend.isRunning}}
            disabled={{this.isResendDisabled}}
            {{on "click" this.resendCode}}
            data-test-phone-verification-resend
          >{{t "phone_verification.code.resend"}}</Button>

          <hr />
          <Button
            @appearance="minimal"
            @size="sm"
            disabled={{this.isBusy}}
            {{on "click" this.deferVerification}}
            data-test-verify-later
          >{{t "phone_verification.code.verify_later"}}</Button>
        {{/if}}

        {{! Mounted whatever the step, because a polite live region has to exist before
            its content arrives or the insertion is not announced. }}
        <div role="status" class="tio-col-loose items-center">
          {{#if (this.isStep "verified")}}
            {{#unless @headless}}
              <IconBadge @icon={{PhDeviceMobile}} @color="teal" @size="lg" />
              <Heading @level={{2}} @size="lg" data-test-verified>{{t
                  "phone_verification.verified.title"
                }}</Heading>
            {{/unless}}
            <p class="tio-text-sm-normal">{{t "phone_verification.verified.body"}}</p>
          {{/if}}
        </div>

        {{#if (this.isStep "deferred")}}
          {{#unless @headless}}
            <IconBadge
              @icon={{PhPaperPlaneTilt}}
              @color="purple"
              @size="lg"
              class="self-start"
              data-test-flow-deferred-hero
            />
            <Heading @level={{2}} @size="lg" data-test-deferred>
              {{if @confirmationTitle @confirmationTitle (t "phone_verification.deferred.title")}}
            </Heading>
          {{/unless}}
          <p class="tio-text-sm-normal" data-test-deferred-body>
            {{if @confirmationBody @confirmationBody (t "phone_verification.deferred.body")}}
          </p>
          <AlertBanner @intent="warning">{{t
              "phone_verification.deferred.unverified_warning"
            }}</AlertBanner>

          {{#if this.isLockedOut}}
            <p
              class="tio-text-sm-normal"
              data-test-phone-verification-lockout-countdown
              {{ticking this.tick}}
            >
              {{t "phone_verification.deferred.locked_for" time=this.lockoutDisplay}}
            </p>
          {{/if}}
        {{/if}}
      </div>
    {{/let}}
  </template>
}
